import { useEffect, useState } from 'react'

/** Hash routing — SharePoint pages own the path; the SPA lives in `#/...`. */
export function getHashPath(): string {
  const raw = window.location.hash.replace(/^#/, '') || '/'
  const path = raw.split('?')[0] || '/'
  return path.startsWith('/') ? path : `/${path}`
}

export function navigate(to: string): void {
  const path = to.startsWith('/') ? to : `/${to}`
  const current = window.location.hash.replace(/^#/, '')
  if (current === path) return
  window.location.hash = path
}

/** Read a query param from the current hash (e.g. `#/clients?q=smith`). */
export function getHashQuery(key: string): string {
  const raw = window.location.hash.replace(/^#/, '')
  const qs = raw.split('?')[1] || ''
  try {
    return new URLSearchParams(qs).get(key) || ''
  } catch {
    return ''
  }
}

export function useHashRoute(): string {
  const [path, setPath] = useState(getHashPath)

  useEffect(() => {
    const onHash = () => setPath(getHashPath())
    window.addEventListener('hashchange', onHash)
    if (!window.location.hash) {
      window.location.hash = '/'
    }
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  return path
}

type RouteMatch = {
  params: Record<string, string>
}

/** Very small path matcher: `/clients/:clientId/:tab?` */
export function matchRoute(pattern: string, path: string): RouteMatch | null {
  const patternParts = pattern.split('/').filter(Boolean)
  const pathParts = path.split('/').filter(Boolean)

  if (patternParts.length !== pathParts.length) return null

  const params: Record<string, string> = {}
  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i]
    const vp = pathParts[i]
    if (pp.startsWith(':')) {
      params[pp.slice(1)] = decodeURIComponent(vp)
    } else if (pp !== vp) {
      return null
    }
  }
  return { params }
}
